import React from 'react';
import { Helmet } from 'react-helmet';
import { JsonLdScript } from 'components/atoms';
import { useSelector } from 'react-redux';
import { HelmetConfig } from 'config/helmet';
import featuredCities from 'config/featuredCities';
import storageTypes from 'config/storageTypes';
import unitsSizes from 'config/unitsSizes';
import useSearchHandler from 'hooks/useSearchHandler';
import usePageViewTracking from 'hooks/usePageViewTracking';
import homepageSelector from 'store/selectors/homepage';

import swerve from 'assets/images/layout/swerve-background-bottom-2.svg';
import logo from 'assets/images/logos/selfstorage-social-logo.png';

import { Page } from 'components/core/Page';
import { Image } from 'components/core/Image';
import {
	HomeHeroBanner,
	WhyPeopleChoose,
	FeaturedCities,
	StorageTypes,
	ArgusHeroBanner,
	StorageUnitSize,
} from 'components/landing';
import { StickySearch } from 'components/search/StickySearch';
import { TrustPilotReviewSection } from 'components/landing/TrustPilotReviewSectionV2';

import './HomePage.scss';
import { PAGE_TYPES } from '@sparefoot/tracking-events';

export function HomePage() {
	const { trackingPixel, meta } = useSelector(homepageSelector);

	const handleSearch = useSearchHandler({});
	usePageViewTracking(PAGE_TYPES.HOME_PAGE);

	const updatedHomepageSchema = [
		{
			'@context': 'https://schema.org',
			'@type': 'Organization',
			'@id': 'https://www.selfstorage.com/#organization',
			name: 'http://SelfStorage.com',
			legalName: 'http://SelfStorage.com',
			description:
				'http://SelfStorage.com is one of the original self-storage comparison shopping sites, launched to help customers find the best deal on storage units. Search over 20,000 storage facilities nationwide, compare prices and unit sizes, and reserve your storage unit for free in minutes -- no credit card required.',
			url: 'https://www.selfstorage.com/',
			logo: {
				'@type': 'ImageObject',
				url: logo,
				width: 1024,
				height: 546,
			},
			foundingDate: '1996',
			founder: {
				'@type': 'Person',
				name: 'Michael McCune',
			},
			address: {
				'@type': 'PostalAddress',
				streetAddress: '10900 Research Blvd 160C PMB 3099',
				addressLocality: 'Austin',
				addressRegion: 'TX',
				postalCode: '78759',
				addressCountry: 'US',
			},
			areaServed: 'US',
			contactPoint: {
				'@type': 'ContactPoint',
				telephone: '+1-512-705-6208',
				contactType: 'customer service',
				areaServed: 'US',
				availableLanguage: 'English',
			},
			sameAs: [
				'https://www.facebook.com/SelfStorageInc/',
				'https://x.com/SelfStorageInc',
				'https://www.pinterest.com/selfstorageinc/',
				'https://www.linkedin.com/company/selfstorage.com/',
				'https://www.crunchbase.com/organization/selfstorage-com-65da',
				'https://www.youtube.com/user/SelfStorageCom',
				'https://www.trustpilot.com/review/www.selfstorage.com/',
			],
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.4',
				bestRating: '5',
				worstRating: '1',
				reviewCount: '78247',
				ratingCount: '78247',
			},
		},
		{
			'@context': 'https://schema.org',
			'@type': 'WebSite',
			'@id': 'https://www.selfstorage.com/#website',
			name: 'http://SelfStorage.com',
			url: 'https://www.selfstorage.com/',
			description:
				'Find and reserve self-storage, car storage, RV storage, boat storage, and more near you with http://SelfStorage.com  -- compare over 20,000 storage facilities nationwide.',
			inLanguage: 'en-US',
			publisher: {
				'@id': 'https://www.selfstorage.com/#organization',
			},
			potentialAction: {
				'@type': 'SearchAction',
				target: 'https://www.selfstorage.com/search?location={search_term_string}',
				'query-input': 'required name=search_term_string',
			},
		},
	];

	return (
		<Page
			className="home-page"
			pageType={PAGE_TYPES.HOME}
			trackingPixel={trackingPixel}
			title={meta.title}
			description={meta.description}
			canonical={meta.canonical}
		>
			<Helmet>
				<meta
					name="google-site-verification"
					content={HelmetConfig.googleSiteVerification}
				/>
				<meta
					name="msvalidate.01"
					content={HelmetConfig.msValidate1}
				/>
			</Helmet>
			<JsonLdScript schema={updatedHomepageSchema} />
			<HomeHeroBanner />

			<StickySearch
				hidden
				horizontal
				onSearch={handleSearch}
			/>
			<ArgusHeroBanner />
			<WhyPeopleChoose />
			<StorageUnitSize units={unitsSizes} />
			<StorageTypes types={storageTypes} />
			<TrustPilotReviewSection />
			<Image
				className="reviews-divider"
				src={swerve}
			/>
			<FeaturedCities cities={featuredCities} />
		</Page>
	);
}

export default HomePage;
